/**
 * Browser history helpers for the app-wide "Zurück" buttons.
 *
 * The app is a server-rendered multi-page app (full page loads, no client-side
 * router) and registers a service worker that answers navigations via fetch(),
 * which leaves document.referrer empty. Neither document.referrer nor
 * window.history.length can reliably tell whether the previous history entry
 * belongs to this app (e.g. history.length === 2 when the only earlier entry is
 * the browser's new-tab page).
 *
 * We therefore stamp every in-app page with an incrementing index in
 * history.state and use that index to decide whether there is an in-app page to
 * return to. The lowest index marks the first in-app page of the tab; from
 * there a history back would leave the app, so goBack() uses the fixed fallback
 * URL instead.
 */

const STATE_KEY = 'phoenixHistoryIndex';
const SESSION_KEY = 'phoenixLastHistoryIndex';

function readLastIndex() {
    try {
        return parseInt(window.sessionStorage.getItem(SESSION_KEY), 10);
    } catch (e) {
        return NaN;
    }
}

function writeLastIndex(index) {
    try {
        window.sessionStorage.setItem(SESSION_KEY, String(index));
    } catch (e) {
        // sessionStorage may be unavailable (private mode/quota) — ignore.
    }
}

/**
 * Stamps the current history entry with an in-app index. Must be called once
 * per full page load from the app bootstrap, before any goBack() can run.
 *
 * A fresh forward navigation gets the next index; returning to an already
 * stamped entry (back/forward) keeps its index.
 *
 * @returns {void}
 */
export function trackPageView() {
    const state = window.history.state || {};
    let index;

    if (typeof state[STATE_KEY] === 'number') {
        // Revisiting an entry we already stamped (back/forward navigation).
        index = state[STATE_KEY];
    } else {
        // Fresh navigation onto a new history entry.
        const last = readLastIndex();
        index = (isNaN(last) ? 0 : last) + 1;
        try {
            window.history.replaceState({...state, [STATE_KEY]: index}, '');
        } catch (e) {
            // replaceState unavailable — goBack() then degrades to the fallback URL.
        }
    }

    writeLastIndex(index);
}

/**
 * Navigates back to the previous in-app page, or to a fixed fallback URL when
 * the current page is the first in-app page of this tab (opened directly, via a
 * bookmark, the PWA start page or from the browser's new-tab page).
 *
 * @param {string} [fallbackUrl] URL to navigate to when there is no in-app page to return to.
 * @returns {void}
 */
export function goBack(fallbackUrl) {
    const state = window.history.state || {};
    const index = state[STATE_KEY];

    // index > 1 means an earlier in-app page exists in this tab's history.
    if (typeof index === 'number' && index > 1) {
        window.history.back();
        return;
    }

    if (fallbackUrl) {
        window.location.href = fallbackUrl;
        return;
    }

    window.history.back();
}



// WEBPACK FOOTER //
// ./resources/assets/js/fn/navigation.js